import React, { useState } from 'react';
import {
  Home,
  PlayCircle,
  FileText,
  RefreshCw,
  Upload,
  Network,
  Users,
  Folder,
  Search as SearchIcon,
  File,
  Loader,
  FolderOpen,
  GitBranch,
  ClipboardList,
  Activity,
  BookOpen
} from 'lucide-react';

const Sidebar = ({ activeSection, activeWorkflow, onSectionChange, onWorkflowChange }) => {
  const [collapsedSections, setCollapsedSections] = useState({
    workflows: false,
    controlPlane: false,
    dataPlane: false,
    workPlane: false
  });

  const toggleSection = (section) => {
    setCollapsedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleSectionClick = (sectionId) => {
    onSectionChange(sectionId);
  };

  const handleWorkflowClick = (workflowId, e) => {
    e.preventDefault();
    onWorkflowChange(workflowId);
  };

  return (
    <aside className="w-64 bg-gray-900 border-r border-gray-700 flex flex-col h-screen overflow-hidden">
      {/* Sticky Header */}
      <div className="flex-shrink-0 p-5 border-b border-gray-700 bg-gray-900">
        <h1 className="text-xl font-bold text-white mb-1">Drawbridge API</h1>
        <p className="text-xs text-gray-400">API Documentation</p>
      </div>

      {/* Search - Only show if implemented, otherwise remove */}
      {/* Uncomment when search is implemented
      <div className="flex-shrink-0 p-4 border-b border-gray-700">
        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search endpoints..."
            className="w-full pl-9 pr-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>
      */}

      {/* Scrollable Navigation */}
      <nav className="flex-1 overflow-y-auto scrollbar-thin px-3 py-4">
        <div className="space-y-1">
          {/* Overview */}
          <a
            href="#overview"
            className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
              activeSection === 'overview'
                ? 'bg-blue-600 text-white'
                : 'text-gray-300 hover:bg-gray-800 hover:text-white'
            }`}
            onClick={(e) => { e.preventDefault(); handleSectionClick('overview'); }}
          >
            <Home className="w-4 h-4" />
            <span>Overview</span>
          </a>

          {/* Workflows Section */}
          <div className="mt-2">
            <button
              onClick={() => toggleSection('workflows')}
              className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider hover:text-gray-300 transition-colors"
            >
              <span>Workflows</span>
              {collapsedSections.workflows ? (
                <FolderOpen className="w-4 h-4" />
              ) : (
                <Folder className="w-4 h-4" />
              )}
            </button>
            {!collapsedSections.workflows && (
              <div className="mt-2 space-y-1 pl-2">
                <a
                  href="#workflow-getting-started"
                  className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
                    activeSection === 'common-workflows' && activeWorkflow === 'getting-started'
                      ? 'bg-blue-600 text-white font-medium'
                      : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                  }`}
                  onClick={(e) => handleWorkflowClick('getting-started', e)}
                >
                  <PlayCircle className="w-4 h-4" />
                  <span className="font-semibold">Generate Your First SOP</span>
                </a>
                <a
                  href="#workflow-sop-refinement"
                  className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
                    activeSection === 'common-workflows' && activeWorkflow === 'sop-refinement'
                      ? 'bg-blue-600 text-white font-medium'
                      : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                  }`}
                  onClick={(e) => handleWorkflowClick('sop-refinement', e)}
                >
                  <RefreshCw className="w-4 h-4" />
                  <span>SOP Refinement</span>
                </a>
                <a
                  href="#workflow-document-ingestion"
                  className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
                    activeSection === 'common-workflows' && activeWorkflow === 'document-ingestion'
                      ? 'bg-blue-600 text-white font-medium'
                      : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                  }`}
                  onClick={(e) => handleWorkflowClick('document-ingestion', e)}
                >
                  <Upload className="w-4 h-4" />
                  <span>Document Ingestion</span>
                </a>
                <a
                  href="#workflow-corpus-map"
                  className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ml-4 ${
                    activeSection === 'common-workflows' && (activeWorkflow === 'corpus-map')
                      ? 'bg-blue-600 text-white font-medium'
                      : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                  }`}
                  onClick={(e) => handleWorkflowClick('corpus-map', e)}
                >
                  <Network className="w-3 h-3" />
                  <span>Structure Exploration</span>
                </a>
              </div>
            )}
          </div>

          {/* Control Plane */}
          <div className="mt-2">
            <button
              onClick={() => toggleSection('controlPlane')}
              className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider hover:text-gray-300 transition-colors"
            >
              <span>Control Plane</span>
              {collapsedSections.controlPlane ? (
                <FolderOpen className="w-4 h-4" />
              ) : (
                <Folder className="w-4 h-4" />
              )}
            </button>
            {!collapsedSections.controlPlane && (
              <div className="mt-2 space-y-1 pl-2">
                <a
                  href="#users"
                  className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
                    activeSection === 'users'
                      ? 'bg-blue-600 text-white font-medium'
                      : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                  }`}
                  onClick={(e) => { e.preventDefault(); handleSectionClick('users'); }}
                >
                  <Users className="w-4 h-4" />
                  <span>Users</span>
                </a>
              </div>
            )}
          </div>

          {/* Data Plane */}
          <div className="mt-2">
            <button
              onClick={() => toggleSection('dataPlane')}
              className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider hover:text-gray-300 transition-colors"
            >
              <span>Data Plane</span>
              {collapsedSections.dataPlane ? (
                <FolderOpen className="w-4 h-4" />
              ) : (
                <Folder className="w-4 h-4" />
              )}
            </button>
            {!collapsedSections.dataPlane && (
              <div className="mt-2 space-y-1 pl-2">
                {/* Resources Group */}
                <div className="space-y-1">
                  <a
                    href="#namespaces"
                    className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
                      activeSection === 'namespaces'
                        ? 'bg-blue-600 text-white font-medium'
                        : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                    }`}
                    onClick={(e) => { e.preventDefault(); handleSectionClick('namespaces'); }}
                  >
                    <FolderOpen className="w-4 h-4" />
                    <span>Namespaces</span>
                  </a>
                </div>

                {/* Content Management Group */}
                <div className="space-y-1 pt-1 border-t border-gray-800">
                  <a
                    href="#documents"
                    className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
                      activeSection === 'documents'
                        ? 'bg-blue-600 text-white font-medium'
                        : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                    }`}
                    onClick={(e) => { e.preventDefault(); handleSectionClick('documents'); }}
                  >
                    <File className="w-4 h-4" />
                    <span>Documents</span>
                  </a>
                  <a
                    href="#ingestion-tasks"
                    className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ml-4 ${
                      activeSection === 'ingestion-tasks'
                        ? 'bg-blue-600 text-white font-medium'
                        : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                    }`}
                    onClick={(e) => { e.preventDefault(); handleSectionClick('ingestion-tasks'); }}
                  >
                    <Loader className="w-3 h-3" />
                    <span>Ingestion Tasks</span>
                  </a>
                </div>

                {/* Discovery & Intelligence Group */}
                <div className="space-y-1 pt-1 border-t border-gray-800">
                  <a
                    href="#search"
                    className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
                      activeSection === 'search'
                        ? 'bg-blue-600 text-white font-medium'
                        : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                    }`}
                    onClick={(e) => { e.preventDefault(); handleSectionClick('search'); }}
                  >
                    <SearchIcon className="w-4 h-4" />
                    <span>Search</span>
                  </a>
                  {/* Knowledge Graph removed */}
                  {/* Corpus Maps hidden */}
                </div>
              </div>
            )}
          </div>

          {/* Work Plane */}
          <div className="mt-2">
            <button
              onClick={() => toggleSection('workPlane')}
              className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider hover:text-gray-300 transition-colors"
            >
              <span>Work Plane</span>
              {collapsedSections.workPlane ? (
                <FolderOpen className="w-4 h-4" />
              ) : (
                <Folder className="w-4 h-4" />
              )}
            </button>
            {!collapsedSections.workPlane && (
              <div className="mt-2 space-y-1 pl-2">
                {/* Organization Group */}
                <div className="space-y-1">
                  <a
                    href="#projects"
                    className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
                      activeSection === 'projects'
                        ? 'bg-blue-600 text-white font-medium'
                        : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                    }`}
                    onClick={(e) => { e.preventDefault(); handleSectionClick('projects'); }}
                  >
                    <FolderOpen className="w-4 h-4" />
                    <span>Projects</span>
                  </a>
                </div>

                {/* SOPs Group */}
                <div className="space-y-1 pt-1 border-t border-gray-800">
                  <a
                    href="#sops"
                    className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
                      activeSection === 'sops'
                        ? 'bg-blue-600 text-white font-medium'
                        : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                    }`}
                    onClick={(e) => { e.preventDefault(); handleSectionClick('sops'); }}
                  >
                    <FileText className="w-4 h-4" />
                    <span>SOPs</span>
                  </a>
                  <a
                    href="#sop-generation"
                    className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ml-2 ${
                      activeSection === 'sop-generation'
                        ? 'bg-blue-600 text-white font-medium'
                        : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                    }`}
                    onClick={(e) => { e.preventDefault(); handleSectionClick('sop-generation'); }}
                  >
                    <FileText className="w-4 h-4" />
                    <span>SOP Generation Tasks</span>
                  </a>
                  <div className="ml-2 space-y-1">
                    <a
                      href="#sop-resources"
                      className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
                        activeSection === 'sop-resources'
                          ? 'bg-blue-600 text-white font-medium'
                          : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                      }`}
                      onClick={(e) => { e.preventDefault(); handleSectionClick('sop-resources'); }}
                    >
                      <ClipboardList className="w-4 h-4" />
                      <span>SOP Management</span>
                    </a>
                    <a
                      href="#sop-proposals"
                      className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ml-4 ${
                        activeSection === 'sop-proposals'
                          ? 'bg-blue-600 text-white font-medium'
                          : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                      }`}
                      onClick={(e) => { e.preventDefault(); handleSectionClick('sop-proposals'); }}
                    >
                      <GitBranch className="w-3 h-3" />
                      <span>Change Proposals</span>
                    </a>
                  </div>
                </div>

                {/* Knowledge Base Group */}
                <div className="space-y-1 pt-1 border-t border-gray-800">
                  <div className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-gray-400">
                    <BookOpen className="w-4 h-4" />
                    <span>Knowledge Base</span>
                    <span className="ml-auto text-xs bg-gray-800 px-2 py-0.5 rounded">Coming Soon</span>
                  </div>
                </div>

                {/* Monitoring Group */}
                <div className="space-y-1 pt-1 border-t border-gray-800">
                  <a
                    href="#logs"
                    className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
                      activeSection === 'logs'
                        ? 'bg-blue-600 text-white font-medium'
                        : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                    }`}
                    onClick={(e) => { e.preventDefault(); handleSectionClick('logs'); }}
                  >
                    <Activity className="w-4 h-4" />
                    <span>Logs</span>
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </nav>
    </aside>
  );
};

export default Sidebar;
